import InstagramBookmarkIcon from '@src/svgs/InstagramBookmarkIcon';
import InstagramCommentIcon from '@src/svgs/InstagramCommentIcon';
import InstagramHeartIcon from '@src/svgs/InstagramHeartIcon';
import InstagramRepostIcon from '@src/svgs/InstagramRepostIcon';
import InstagramShareIcon from '@src/svgs/InstagramShareIcon';
import InstagramVerifiedBadge from '@src/svgs/InstagramVerifiedBadge';
import React from 'react';
import styled from 'styled-components';

import { htmlToPlainText } from './htmlToPlainText';
import { renderBodyWithLinks } from './renderBodyWithLinks';

/** Distribution channel slug this preview renders (PLAT-11038). */
export const CHANNEL_SLUG = 'instagram';

type InstagramViewport = 'desktop' | 'mobile';

type Props = {
  content: string;
  viewport?: InstagramViewport;
  authorName?: string;
  avatarUrl?: string;
};

const CHAR_LIMIT = 2200;
const DESKTOP_LINE_LIMIT = 1;
const MOBILE_LINE_LIMIT = 2;

const InstagramPreview: React.FC<Props> = ({
  content,
  viewport = 'desktop',
  authorName,
  avatarUrl,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isClipped, setIsClipped] = React.useState(false);
  const textRef = React.useRef<HTMLDivElement>(null);

  const plainText = htmlToPlainText(content);

  const isOverLimit = plainText.length > CHAR_LIMIT;
  const overflowAmount = Math.max(0, plainText.length - CHAR_LIMIT);

  const isMobile = viewport === 'mobile';
  const lineLimit = isMobile ? MOBILE_LINE_LIMIT : DESKTOP_LINE_LIMIT;

  React.useLayoutEffect(() => {
    if (isExpanded || !textRef.current) {
      setIsClipped(false);
      return;
    }
    const el = textRef.current;
    setIsClipped(el.scrollHeight > el.clientHeight + 1);
  }, [plainText, lineLimit, isExpanded]);

  const handle = authorName || 'your_handle';
  const timestamp = isMobile ? '21m' : '3h';

  return (
    <Container>
      <PreviewCard viewport={viewport}>
        <PostHeader isMobile={isMobile}>
          <AvatarRing>
            <AvatarInner>
              {avatarUrl ? (
                <AvatarImage src={avatarUrl} alt={handle} />
              ) : (
                <AvatarPlaceholder />
              )}
            </AvatarInner>
          </AvatarRing>
          <HeaderRow>
            <Handle>{handle}</Handle>
            <HeaderVerified aria-label="Verified account">
              <InstagramVerifiedBadge width="14" height="14" />
            </HeaderVerified>
            <MetaDot>·</MetaDot>
            <Timestamp>{timestamp}</Timestamp>
          </HeaderRow>
          <MoreButton aria-label="More options">⋯</MoreButton>
        </PostHeader>

        <ActionRow isMobile={isMobile}>
          <ActionGroup>
            <ActionItem>
              <ActionIconBtn aria-label="Like">
                <InstagramHeartIcon
                  width="24"
                  height="24"
                  fillColor="#262626"
                />
              </ActionIconBtn>
              {!isMobile && <ActionCount>24.8K</ActionCount>}
            </ActionItem>
            <ActionItem>
              <ActionIconBtn aria-label="Comment">
                <InstagramCommentIcon
                  width="24"
                  height="24"
                  fillColor="#262626"
                />
              </ActionIconBtn>
              {!isMobile && <ActionCount>412</ActionCount>}
            </ActionItem>
            <ActionItem>
              <ActionIconBtn aria-label="Repost">
                <InstagramRepostIcon
                  width="24"
                  height="24"
                  fillColor="#262626"
                />
              </ActionIconBtn>
              {!isMobile && <ActionCount>89</ActionCount>}
            </ActionItem>
            <ActionItem>
              <ActionIconBtn aria-label="Share">
                <InstagramShareIcon
                  width="24"
                  height="24"
                  fillColor="#262626"
                />
              </ActionIconBtn>
            </ActionItem>
          </ActionGroup>
          <ActionIconBtn aria-label="Save">
            <InstagramBookmarkIcon width="24" height="24" fillColor="#262626" />
          </ActionIconBtn>
        </ActionRow>

        <CaptionBlock isMobile={isMobile}>
          <CaptionWrapper>
            <ClampedCaption
              ref={textRef}
              clamped={!isExpanded}
              lines={lineLimit}
            >
              <CaptionHandle>{handle}</CaptionHandle>
              <CaptionVerified aria-label="Verified account">
                <InstagramVerifiedBadge width="12" height="12" />
              </CaptionVerified>{' '}
              {renderBodyWithLinks(plainText, BlueLink)}
              {isExpanded && (
                <>
                  {' '}
                  <CaptionLessInline
                    onClick={() => setIsExpanded(false)}
                    style={{ color: '#8e8e8e' }}
                  >
                    less
                  </CaptionLessInline>
                </>
              )}
            </ClampedCaption>
            {isClipped && !isExpanded && isMobile && (
              <CaptionMoreInline onClick={() => setIsExpanded(true)}>
                ...<span style={{ color: '#8e8e8e' }}> more</span>
              </CaptionMoreInline>
            )}
          </CaptionWrapper>
          {isClipped && !isExpanded && !isMobile && (
            <CaptionMoreBlock
              onClick={() => setIsExpanded(true)}
              style={{ color: '#8e8e8e' }}
            >
              more
            </CaptionMoreBlock>
          )}
        </CaptionBlock>
      </PreviewCard>

      <CharacterCount overLimit={isOverLimit}>
        {plainText.length.toLocaleString()} / {CHAR_LIMIT.toLocaleString()}{' '}
        characters
        {isOverLimit && <> · {overflowAmount.toLocaleString()} over</>}
      </CharacterCount>
    </Container>
  );
};

export default InstagramPreview;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PreviewCard = styled.div<{ viewport: 'desktop' | 'mobile' }>`
  background: white;
  border-radius: ${props => (props.viewport === 'mobile' ? '0' : '4px')};
  overflow: hidden;
  width: 100%;
  max-width: ${props => (props.viewport === 'mobile' ? '375px' : '470px')};
  margin: 0 auto;
  border: 1px solid #dbdbdb;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial,
    sans-serif;
  color: #262626;
`;

const PostHeader = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${props => (props.isMobile ? '10px 12px' : '12px 16px')};
`;

const AvatarRing = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  padding: 2px;
  background: linear-gradient(
    45deg,
    #f09433 0%,
    #e6683c 25%,
    #dc2743 50%,
    #cc2366 75%,
    #bc1888 100%
  );
  flex-shrink: 0;
`;

const AvatarInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: white;
  padding: 2px;
  box-sizing: border-box;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;

const AvatarPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg, #c13584 0%, #833ab4 100%);
`;

const HeaderRow = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  line-height: 1.2;
`;

const Handle = styled.span`
  font-weight: 600;
  color: #262626;
`;

const HeaderVerified = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const MetaDot = styled.span`
  color: #737373;
  font-size: 14px;
`;

const Timestamp = styled.span`
  color: #737373;
  font-size: 14px;
`;

const MoreButton = styled.button`
  background: transparent;
  border: none;
  color: #262626;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
`;

const ActionRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => (props.isMobile ? '6px 8px 4px' : '8px 12px 4px')};
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ActionItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const ActionIconBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #262626;

  &:hover {
    color: #737373;
  }

  & > svg {
    display: block;
  }
`;

const ActionCount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  margin-right: 8px;
`;

const CaptionBlock = styled.div<{ isMobile: boolean }>`
  padding: ${props => (props.isMobile ? '0 12px 12px' : '0 16px 12px')};
  font-size: 14px;
  line-height: 1.4;
  color: #262626;
`;

const CaptionWrapper = styled.div`
  position: relative;
`;

const ClampedCaption = styled.div<{ clamped: boolean; lines: number }>`
  white-space: pre-wrap;
  word-wrap: break-word;
  ${props =>
    props.clamped &&
    `
    display: -webkit-box;
    -webkit-line-clamp: ${props.lines};
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

const CaptionHandle = styled.span`
  font-weight: 600;
  color: #262626;
  margin-right: 4px;
`;

const CaptionVerified = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-right: 2px;
`;

const BlueLink = styled.span`
  color: #00376b;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const CaptionMoreInline = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  padding-left: 24px;
  background: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff 30%);
  cursor: pointer;
  color: #262626;
  font-weight: 400;
`;

const CaptionLessInline = styled.span`
  cursor: pointer;
  color: #8e8e8e;
  font-weight: 400;
`;

const CaptionMoreBlock = styled.span`
  display: inline-block;
  margin-top: 2px;
  cursor: pointer;
  color: #a8a8a8;
  font-weight: 400;
`;

const CharacterCount = styled.div<{ overLimit: boolean }>`
  text-align: right;
  font-size: 11px;
  color: ${props => (props.overLimit ? '#d32f2f' : 'var(--text-muted)')};
  font-weight: ${props => (props.overLimit ? '600' : '400')};
  padding: 0 4px;
`;
